import moment from "moment";

import { formatMoney } from "../../../../helpers/formatter";
import { IEnumDto } from "../../../../interfaces/funderInvestment";
import { getCurrencyInfo } from "../../helper";

/** One rate band, as `GET /eco/investment_product/:id` sends it. */
export type TSavedChartSlab = {
  id?: number;
  from_amount?: number | null;
  to_amount?: number | null;
  interest_rate?: number | null;
  description?: string | null;
};

/** One currency of a saved fund. */
export type TSavedFundDetail = {
  id: number;
  currency_code: string;
  deposit_type?: string | number | null;
  core_banking_tenant_id?: string | null;
  core_banking_product_id?: number | null;
  minimum_investment?: number | null;
  maximum_investment?: number | null;
  investment_size?: number | null;
  terms_and_condition_id?: number | null;
  early_liquidation_charge_percentage?: number | null;
  investment_interest_payment_type?: { id?: number; name?: string } | null;
  interest_rate_chart?: {
    name?: string | null;
    from_date?: string | null;
    to_date?: string | null;
    chart_slabs?: TSavedChartSlab[];
  } | null;
};

/** A saved fund, as `GET /eco/investment_product/:id` sends it. */
export type TSavedFund = {
  id: number;
  name: string;
  description?: string | null;
  objective?: string | null;
  issuers?: string | null;
  target_investors?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_customer_facing?: boolean | null;
  investment_product_type_name?: string | null;
  investment_product_sub_type_name?: string | null;
  investment_product_sub_type_detail_name?: string | null;
  product_type_id?: IEnumDto | null;
  withdrawal_interval?: IEnumDto | null;
  withdrawal_interval_unit?: IEnumDto | null;
  grace_periods?: IEnumDto | null;
  term_sheet_url?: string | null;
  term_sheet_filename?: string | null;
  investment_product_details?: TSavedFundDetail[];
};

/** Where uploaded term sheets are served from. */
export const ADVANCLY_STORAGE_URL =
  "https://advanclystorage.blob.core.windows.net/advancly/";

/** True when there is nothing to show — the screen says "Not set" instead. */
export const isBlank = (value: unknown) =>
  value === null || value === undefined || String(value).trim() === "";

/** "2026-10-01T00:00:00" → "01 Oct 2026". */
export const formatDate = (value?: string | null) =>
  value ? moment(value).format("DD MMM YYYY") : undefined;

/** "1 – 8 Oct 2026", collapsing the month and year the two dates share. */
export function formatDateRange(from?: string | null, to?: string | null) {
  if (!from || !to) {
    return undefined;
  }

  const start = moment(from);
  const end = moment(to);

  if (start.isSame(end, "month")) {
    return `${start.format("D")} – ${end.format("D MMM YYYY")}`;
  }

  return start.isSame(end, "year")
    ? `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`
    : `${start.format("D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
}

/** An amount in the currency's own symbol, or nothing when there is none. */
export const formatAmount = (
  value: number | null | undefined,
  currency: string,
) =>
  isBlank(value)
    ? undefined
    : formatMoney(Number(value), getCurrencyInfo(currency)?.symbol);

/** "SavingsDeposit" → "Savings deposit". */
export const humanise = (value?: string | number | null) => {
  if (isBlank(value)) {
    return undefined;
  }

  const words = String(value)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The plan type's display name, from the EnumDto the API sends. */
export const planTypeName = (fund?: TSavedFund) =>
  fund?.product_type_id?.description ?? humanise(fund?.product_type_id?.name);
